import React from "react"
import { Sprout, User, Phone, ChevronDown, Loader2 } from "lucide-react"
import { Field, inputCls, selectCls } from "./Field"
import { TreeInventory } from "./TreeInventory"
import { GPSCapture } from "./GPSCapture"
import { SuccessState } from "./SuccessState"
import { FormData, TreeEntry, FormErrors } from "../types"
import { FARM_SIZES, ARABLE_SIZES, TREE_COVERAGE } from "../constants"

interface RegistrationFormProps {
  form: FormData
  errors: FormErrors
  submitted: boolean
  loading: boolean
  treeInput: string
  regions: Array<{id: number, name: string}>
  treeTypes: Array<{id: number, name: string}>
  onTreeInputChange: (v: string) => void
  onFieldChange: (key: keyof FormData, value: string) => void
  onAddTree: (name: string) => void
  onRemoveTree: (id: string) => void
  onUpdateTree: (id: string, key: keyof TreeEntry, value: string) => void
  onSubmit: (e: React.FormEvent) => void
  onReset: () => void
}

export const RegistrationForm: React.FC<RegistrationFormProps> = ({
  form, errors, submitted, loading,
  treeInput, regions, treeTypes, onTreeInputChange,
  onFieldChange, onAddTree, onRemoveTree, onUpdateTree,
  onSubmit, onReset,
}) => {
  if (submitted) return <SuccessState onReset={onReset} />

  return (
    <form
      onSubmit={onSubmit}
      className="bg-[#1e1e1e]/90 backdrop-blur-md border border-white/10 rounded-2xl p-8 space-y-6"
    >
      <div>
        <h2 className="text-xl font-bold text-white mb-0.5">Farmer Registration</h2>
        <p className="text-neutral-500 text-xs">All fields marked * are required</p>
      </div>

      {/* Name + Phone */}
      <div className="grid grid-cols-2 gap-5">
        <Field label="Full Name" required>
          <div className="relative">
            <User className="absolute left-0 bottom-2.5 w-3.5 h-3.5 text-neutral-600" />
            <input
              className={`${inputCls} pl-5 ${errors.name ? "border-red-500" : ""}`}
              placeholder="John Kamau"
              value={form.name}
              onChange={e => onFieldChange("name", e.target.value)}
            />
          </div>
          {errors.name && (
            <span className="text-red-400 text-[10px] mt-0.5">{errors.name}</span>
          )}
        </Field>

        <Field label="Phone" required>
          <div className="relative">
            <Phone className="absolute left-0 bottom-2.5 w-3.5 h-3.5 text-neutral-600" />
            <input
              className={`${inputCls} pl-5 ${errors.phone ? "border-red-500" : ""}`}
              placeholder="+254 7xx xxx xxx"
              value={form.phone}
              onChange={e => onFieldChange("phone", e.target.value)}
            />
          </div>
          {errors.phone && (
            <span className="text-red-400 text-[10px] mt-0.5">{errors.phone}</span>
          )}
        </Field>
      </div>

      {/* Region */}
      <Field label="Region" required>
        <div className="relative">
          <select
            className={`${selectCls} ${errors.region ? "border-red-500" : ""}`}
            value={form.region}
            onChange={e => onFieldChange("region", e.target.value)}
          >
            <option value="" className="bg-[#1e1e1e]">— Select Region —</option>
            {regions.map(r => (
              <option key={r.id} value={r.id.toString()} className="bg-[#1e1e1e]">{r.name}</option>
            ))}
          </select>
          <ChevronDown className="absolute right-0 bottom-2.5 w-3.5 h-3.5 text-neutral-600 pointer-events-none" />
        </div>
        {errors.region && (
          <span className="text-red-400 text-[10px] mt-0.5">{errors.region}</span>
        )}
      </Field>

      {/* Farm Sizes */}
      <div className="grid grid-cols-3 gap-4">
        {([
          { label: "Farm Size",     key: "farmSize",     opts: FARM_SIZES },
          { label: "Arable Land",   key: "arableSize",   opts: ARABLE_SIZES },
          { label: "Tree Coverage", key: "treeCoverage", opts: TREE_COVERAGE },
        ] as const).map(({ label, key, opts }) => (
          <Field key={key} label={label}>
            <div className="relative">
              <select
                className={`${selectCls} text-[11px]`}
                value={form[key]}
                onChange={e => onFieldChange(key, e.target.value)}
              >
                <option value="" className="bg-[#1e1e1e]">— Select —</option>
                {opts.map(o => (
                  <option key={o} value={o} className="bg-[#1e1e1e]">{o}</option>
                ))}
              </select>
              <ChevronDown className="absolute right-0 bottom-2.5 w-3 h-3 text-neutral-600 pointer-events-none" />
            </div>
          </Field>
        ))}
      </div>

      {/* Tree Inventory */}
      <Field label="Tree Types on Farm">
        <TreeInventory
          trees={form.trees}
          treeInput={treeInput}
          treeTypes={treeTypes}
          onInputChange={onTreeInputChange}
          onAdd={onAddTree}
          onRemove={onRemoveTree}
          onUpdate={onUpdateTree}
        />
        {errors.trees && (
          <span className="text-red-400 text-[10px] mt-0.5">{errors.trees}</span>
        )}
      </Field>

      {/* GPS Location */}
      <Field label="Farm Location" required>
        <GPSCapture
          latitude={form.latitude}
          longitude={form.longitude}
          onLatChange={v => onFieldChange("latitude", v)}
          onLngChange={v => onFieldChange("longitude", v)}
        />
        {errors.latitude && (
          <span className="text-red-400 text-[10px] mt-1">{errors.latitude}</span>
        )}
      </Field>

      {/* General error */}
      {errors.general && (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-4 py-3">
          <p className="text-red-400 text-xs">{errors.general}</p>
        </div>
      )}

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-400 disabled:bg-emerald-500/50 text-black font-bold text-sm rounded-xl transition-all hover:scale-[1.01] active:scale-[0.99] flex items-center justify-center gap-2"
      >
        {loading
          ? <><Loader2 className="w-4 h-4 animate-spin" /> Submitting...</>
          : <><Sprout className="w-4 h-4" /> Register My Farm</>
        }
      </button>

      <p className="text-center text-[10px] text-neutral-600">
        Your data is protected and used solely for national tree inventory purposes.
      </p>
    </form>
  )
}